// filepath: /src/sections/LicenseSection.js
import React, { useState, useEffect } from 'react';
import './LicenseSection.css';
import urls from '../config/urls';
import licensesData from '../data/licenses.json';
import { 
  PlanFormModal, 
  InstitutionFormModal, 
  PaymentFormModal 
} from '../components/Formularios';
import registrationService from '../services/registrationService';

// Modal de confirmación con mejor estética
const ModalConfirmacion = ({ isOpen, onClose, title, message, isSuccess = true }) => {
  if (!isOpen) return null;
  
  const formatMessage = (msg) => {
    if (!msg) return 'Tu pago se ha procesado correctamente.';
    
    // Dividir el mensaje en partes
    const parts = msg.split('\n');
    
    return parts.map((part, index) => {
      if (part.includes('Revisa tu correo') || part.includes('correo')) {
        return (
          <div key={index} className="email-verification-notice">
            <div className="verification-icon">📧</div>
            <p className="verification-text">{part}</p>
          </div>
        );
      } else if (part.includes('Licencia:')) {
        return <div key={index} className="info-line license-info">🎓 {part}</div>;
      } else if (part.includes('Usuario:')) {
        return <div key={index} className="info-line user-info">👤 {part}</div>;
      } else if (part.includes('Institución:')) {
        return <div key={index} className="info-line institution-info">🏢 {part}</div>;
      } else if (part.includes('Error al procesar') || part.includes('Error:')) {
        return <div key={index} className="error-line">❌ {part}</div>;
      } else if (part.includes('$') || part.includes('MXN') || part.includes('Monto:')) {
        return <div key={index} className="info-line price-info">💰 {part}</div>;
      } else if (part.includes('Por favor') || part.includes('intenta') || part.includes('revisa')) {
        return <div key={index} className="info-line warning-info">⚠️ {part}</div>;
      } else if (part.includes('Usuario registrado exitosamente') || part.includes('procesada exitosamente')) {
        return <div key={index} className="info-line success-info">✅ {part}</div>;
      } else if (part.trim() === '') {
        return <br key={index} />;
      } else {
        return <div key={index} className="info-line">{part}</div>;
      }
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header confirmation-header">
          <div className="success-icon">
            {isSuccess ? '✅' : '❌'}
          </div>
          <h2>{title || (isSuccess ? '¡Registro Exitoso!' : 'Error en el Proceso')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body confirmation-body">
          <div className="confirmation-content">
            {formatMessage(message)}
          </div>
        </div>
        <div className="modal-actions">
          <button className="confirmation-button" onClick={onClose}>
            {isSuccess ? 'Entendido' : 'Intentar de Nuevo'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LicenseSection = () => {
  // ===== CONFIGURACIÓN DE PRECIOS =====
  // Cambia estos flags para controlar qué mostrar:
  const SHOW_TOGGLE = false; // true = mostrar toggle mensual/anual, false = mostrar solo un precio
  const DEFAULT_PRICING_MODE = 'annual'; // 'monthly' | 'annual' - solo usado cuando SHOW_TOGGLE = false
  
  const [isVisible, setIsVisible] = useState(false);
  const [isAnnual, setIsAnnual] = useState(SHOW_TOGGLE ? false : DEFAULT_PRICING_MODE === 'annual');
  
  // Estados para el flujo de formularios
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isInstitutionModalOpen, setIsInstitutionModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isOperationSuccessful, setIsOperationSuccessful] = useState(true);
  const [formData, setFormData] = useState({
    // Datos personales básicos (Paso 1)
    curp: '',
    name: '',
    paternalLastName: '',
    maternalLastName: '',
    gender: '',
    birthDate: '',
    birthState: '',
    // Información de perfil (Paso 1)
    academicFormation: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    acceptTerms: false,
    // Datos de institución (Paso 2)
    institutionName: '',
    institutionType: '',
    institutionSize: '',
    institutionSpecialty: '',
    // Domicilio de institución
    billingStreet: '',
    billingExteriorNumber: '',
    billingInteriorNumber: '',
    billingNeighborhood: '',
    billingMunicipality: '',
    billingState: '',
    billingPostalCode: '',
    billingCountry: 'México',
    // RFC para facturación
    institutionRFC: '',
    // Contacto de institución
    institutionPhone: '',
    institutionEmail: '',
    website: '',
    // Logo y términos
    logo: null,
    acceptInstitutionTerms: false,
    // Datos de pago (Paso 3)
    paymentMethod: 'card',
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVC: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    const section = document.getElementById('licencias');
    if (section) {
      observer.observe(section);
    }

    // Fallback para móviles
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => {
      if (section) {
        observer.unobserve(section);
      }
      clearTimeout(timer);
    };
  }, []);

  const licenses = licensesData.licenses.map(license => ({
    ...license,
    redirectUrl: urls.licenses[license.id] || urls.app
  }));

  const { metadata } = licensesData;
  const { freeTrial } = metadata;

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Función para generar el texto de prueba gratuita
  const getTrialText = () => {
    if (!freeTrial || !freeTrial.enabled) return null;
    
    const { duration, period, text, fullText, emoji } = freeTrial;
    
    return {
      badge: `${emoji || '🎁'} ${text}`,
      description: fullText,
      period: period,
      duration: duration
    };
  };

  // Funciones para el manejo de inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted;
  };

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  // Función para iniciar el flujo de formularios
  const handleLicenseRequest = (license) => {
    setSelectedLicense(license);
    setIsPlanModalOpen(true);
  };

  // Manejo del flujo de pasos
  const handlePlanFormSubmit = (e) => {
    e.preventDefault();
    setIsPlanModalOpen(false);
    setIsInstitutionModalOpen(true);
  };

  const handleInstitutionFormSubmit = (e) => {
  e.preventDefault();
  // Validar campos requeridos antes de avanzar
  // Si quieres validación extra, agrégala aquí
  // Si todo está bien, avanzar
  setIsInstitutionModalOpen(false);
  setIsPaymentModalOpen(true);
  // Si necesitas mostrar error, puedes usar un estado para el mensaje y no cerrar el modal
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const finalIsAnnual = SHOW_TOGGLE ? isAnnual : DEFAULT_PRICING_MODE === 'annual';
      const userData = {
        curp: formData.curp,
        nombre: formData.name,
        apellidoPaterno: formData.paternalLastName,
        apellidoMaterno: formData.maternalLastName,
        sexo: formData.gender === 'M' ? '1' : '2',
        fechaNacimiento: formData.birthDate,
        entidad: '15',
        rol: '857',
        telefono: formData.phone,
        email: formData.email,
        password: formData.password
      };
      const institutionData = {
        nombre: formData.institutionName,
        tipo: formData.institutionType,
        telefono: formData.institutionPhone,
        email: formData.institutionEmail,
        direccion: {
          calle: formData.billingStreet || '',
          numeroExterior: formData.billingExteriorNumber || '',
          numeroInterior: formData.billingInteriorNumber || '',
          colonia: formData.billingNeighborhood || '',
          municipio: formData.billingMunicipality || '',
          estado: formData.billingState || '',
          codigoPostal: formData.billingPostalCode || '',
          pais: formData.billingCountry || 'México'
        },
        rfc: formData.institutionRFC || '',
        pais: formData.billingCountry || 'México'
      };
      const paymentData = {
        planId: selectedLicense.id || 'license',
        planName: selectedLicense.name,
        amount: finalIsAnnual ? selectedLicense.annualPrice : selectedLicense.monthlyPrice,
        currency: 'MXN',
        metodoPago: formData.paymentMethod,
        tarjeta: formData.paymentMethod === 'card' ? {
          numero: formData.cardNumber,
          titular: formData.cardName,
          expiracion: formData.cardExpiry,
          cvv: formData.cardCVC
        } : null
      };
      
      // Llamar al servicio y obtener la respuesta
      const response = await registrationService.registerUser(
        userData,
        institutionData,
        paymentData
      );
      
      // Validar que la respuesta sea exitosa
      const isSuccessful = response && response.data && response.message;
      const paymentAmount = finalIsAnnual ? selectedLicense.annualPrice || selectedLicense.yearlyPrice : selectedLicense.monthlyPrice;
      const paymentPeriod = finalIsAnnual ? 'anual' : 'mensual';
      
      if (isSuccessful) {
        // Registro exitoso - usar el mensaje del servicio
        setConfirmationMessage(
          `¡Solicitud de licencia procesada exitosamente!\n\nLicencia: ${selectedLicense.name}\nUsuario: ${formData.name} ${formData.paternalLastName}\nInstitución: ${formData.institutionName}\nMonto: ${formatPrice(paymentAmount)} (${paymentPeriod})\n\n${response.message}`
        );
        setIsOperationSuccessful(true);
        setShowConfirmationModal(true);
      } else {
        // Error en el servicio - mostrar mensaje de error del backend
        const errorMessage = response?.message || 'Error desconocido en el servicio';
        setConfirmationMessage(`Error al procesar la solicitud:\n\n${errorMessage}\n\nPor favor, revisa la información e intenta nuevamente.`);
        setIsOperationSuccessful(false);
        setShowConfirmationModal(true);
      }
    } catch (error) {
      console.error('❌ Error en registro:', error);
      
      // Manejar diferentes tipos de errores
      let errorMessage = 'Error de conexión o del servidor';
      
      if (error.response) {
        // Error de respuesta del servidor (4xx, 5xx)
        if (typeof error.response.data === 'object' && error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          // Si es un string tipo 'Error 409: {...}', extrae el JSON y muestra solo message
          const match = error.response.data.match(/\{.*\}/);
          if (match) {
            try {
              const parsed = JSON.parse(match[0]);
              if (parsed && parsed.message) {
                errorMessage = parsed.message;
              } else {
                errorMessage = error.response.data;
              }
            } catch {
              errorMessage = error.response.data;
            }
          } else {
            // Si es un string tipo JSON, intenta parsear y extraer message
            try {
              const parsed = JSON.parse(error.response.data);
              if (parsed && parsed.message) {
                errorMessage = parsed.message;
              } else {
                errorMessage = error.response.data;
              }
            } catch {
              errorMessage = error.response.data;
            }
          }
        } else {
          errorMessage = error.response.data?.error || `Error del servidor: ${error.response.status}`;
        }
      } else if (error.request) {
        // Error de red (no hay respuesta)
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
      } else if (error.message) {
        // Error de configuración u otro
        errorMessage = error.message;
      }
      
  setConfirmationMessage(errorMessage);
      setIsOperationSuccessful(false);
      setShowConfirmationModal(true);
    }
    // Resetear todo el flujo
    resetAllForms();
  };

  const resetAllForms = () => {
    setIsPaymentModalOpen(false);
    setFormData({
      // Datos personales básicos (Paso 1)
      curp: '',
      name: '',
      paternalLastName: '',
      maternalLastName: '',
      gender: '',
      birthDate: '',
      birthState: '',
      // Información de perfil (Paso 1)
      academicFormation: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      company: '',
      acceptTerms: false,
      // Datos de institución (Paso 2)
      institutionName: '',
      institutionType: '',
      institutionSize: '',
      institutionSpecialty: '',
      // Domicilio de institución
      billingStreet: '',
      billingExteriorNumber: '',
      billingInteriorNumber: '',
      billingNeighborhood: '',
      billingMunicipality: '',
      billingState: '',
      billingPostalCode: '',
      billingCountry: 'México',
      // RFC para facturación
      institutionRFC: '',
      // Contacto de institución
      institutionPhone: '',
      institutionEmail: '',
      website: '',
      // Logo y términos
      logo: null,
      acceptInstitutionTerms: false,
      // Datos de pago (Paso 3)
      paymentMethod: 'card',
      cardNumber: '',
      cardName: '',
      cardExpiry: '',
      cardCVC: ''
    });
    setSelectedLicense(null);
  };

  // Funciones para cerrar modales
  const closePlanModal = () => {
    setIsPlanModalOpen(false);
    setSelectedLicense(null);
  };

  const closeInstitutionModal = () => {
    setIsInstitutionModalOpen(false);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
  };

  // Funciones para navegar entre pasos (botones de "Atrás")
  const goBackToUserForm = () => {
    setIsInstitutionModalOpen(false);
    setIsPlanModalOpen(true);
  };

  const goBackToInstitutionForm = () => {
    setIsPaymentModalOpen(false);
    setIsInstitutionModalOpen(true);
  };

  const renderIcon = (iconType) => {
    const icons = {
      shield: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L3.5 6.5v10c0 5.25 3.5 10 8.5 10s8.5-4.75 8.5-10v-10L12 2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      document: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      support: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M13 8H8v6h5V8z" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      )
    };
    return icons[iconType] || icons.shield;
  };

  return (
    <>
      <section id="licencias" className={`license-section ${isVisible ? 'visible' : ''}`}>
        <div className="license-background">
          <div className="license-orb orb-1"></div>
          <div className="license-orb orb-2"></div>
          <div className="license-orb orb-3"></div>
        </div>

        <div className="container">
          <div className="license-header">
            <div className="section-badge">
              <span className="badge-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L3 7h2v5h6V7h2L8 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {metadata.badge}
            </div>
            <h2 className="section-title">
              Licencias 
              <span className="gradient-text"> Educativas y de Investigación</span>
            </h2>
            <p className="section-subtitle">
              {metadata.subtitle}
            </p>
          </div>

          {/* Toggle Mensual/Anual - Solo mostrar si SHOW_TOGGLE es true */}
          {SHOW_TOGGLE && (
            <div className="license-toggle">
              <span className={`toggle-label ${!isAnnual ? 'active' : ''}`}>Mensual</span>
              <div className="toggle-switch" onClick={() => setIsAnnual(!isAnnual)}>
                <div className={`toggle-slider ${isAnnual ? 'annual' : ''}`}></div>
              </div>
              <span className={`toggle-label ${isAnnual ? 'active' : ''}`}>
                Anual
                <span className="savings-badge">Ahorra {metadata.savingsPercentage}%</span>
              </span>
            </div>
          )}

          {/* Mostrar información del modo fijo cuando no hay toggle */}
          {!SHOW_TOGGLE && (
            <div className="license-mode-info">
              <div className="fixed-mode-badge">
                <span className="mode-text">
                  {DEFAULT_PRICING_MODE === 'annual' ? '💰 Precios Anuales' : '📅 Precios Mensuales'}
                </span>
                {DEFAULT_PRICING_MODE === 'annual' && (
                  <span className="savings-text">
                    - Ahorra {metadata.savingsPercentage}%
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="license-grid">
            {licenses.map((license, index) => (
              <div 
                key={license.id}
                className={`license-card glass-card ${license.popular ? 'popular' : ''}`}
              >
                {license.popular && (
                  <div className="popular-badge">
                    <span>Más Solicitada</span>
                  </div>
                )}
                
                <div className="license-header-card">
                   {/* Mostrar información de prueba gratuita */}
                  {(() => {
                    const trialInfo = getTrialText();
                    return trialInfo && license.monthlyPrice > 0 && (
                      <div className="annual-savings" style={{ 
                        background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 142, 83, 0.15) 100%)', 
                        border: '2px solid rgba(255, 107, 107, 0.4)',
                        borderRadius: '12px',
                        color: '#ff6b6b',
                        marginBottom: '15px',
                        padding: '10px 15px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        boxShadow: '0 4px 12px rgba(255, 107, 107, 0.2)',
                        textAlign: 'center'
                      }}>
                        🎁{trialInfo.emoji} {trialInfo.description}
                      </div>
                    );
                  })()}
                  <h3 className="license-name">{license.name}</h3>
                  <p className="license-description">{license.description}</p>
                  
                 
                  
                  <div className="license-price">
                    <span className="price-amount">
                      {(SHOW_TOGGLE ? isAnnual : DEFAULT_PRICING_MODE === 'annual') ? formatPrice(license.yearlyPrice) : formatPrice(license.monthlyPrice)}
                    </span>
                    {license.monthlyPrice > 0 && (
                      <span className="price-period">
                        {(SHOW_TOGGLE ? isAnnual : DEFAULT_PRICING_MODE === 'annual') ? '/año' : '/mes'}
                      </span>
                    )}
                  </div>
                  
                  {/* Solo mostrar información adicional de precios si hay toggle */}
                  {SHOW_TOGGLE && isAnnual && license.monthlyPrice > 0 && (
                    <div className="annual-savings">
                      Ahorras {formatPrice(license.monthlyPrice * 12 - license.yearlyPrice)} al año
                    </div>
                  )}
                  
                  {SHOW_TOGGLE && !isAnnual && license.monthlyPrice > 0 && (
                    <div className="yearly-price">
                      {formatPrice(license.yearlyPrice)} pagado anualmente
                    </div>
                  )}
                </div>

                <div className="license-features">
                  <ul>
                    {license.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="license-action">
                  <button 
                    className={`glass-button ${license.popular ? 'primary' : 'secondary'} full-width`}
                    onClick={() => handleLicenseRequest(license)}
                  >
                    {license.buttonText}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="license-footer">
            <div className="info-cards">
              {metadata.infoCards.map((card, index) => (
                <div key={index} className="info-card">
                  <div className="info-icon">
                    {renderIcon(card.icon)}
                  </div>
                  <div className="info-text">
                    <h4>{card.title}</h4>
                    <p>{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Modal de Formulario de Usuario - Paso 1 */}
      <PlanFormModal
        isOpen={isPlanModalOpen}
        onClose={closePlanModal}
        onSubmit={handlePlanFormSubmit}
        selectedPlan={selectedLicense}
        formData={formData}
        handleInputChange={handleInputChange}
        stepInfo={{ current: 1, total: 3, stepName: "Datos Personales" }}
      />

      {/* Modal de Formulario de Institución - Paso 2 */}
      <InstitutionFormModal
        isOpen={isInstitutionModalOpen}
        onClose={closeInstitutionModal}
        onSubmit={handleInstitutionFormSubmit}
        onBack={goBackToUserForm}
        selectedPlan={selectedLicense}
        formData={formData}
        handleInputChange={handleInputChange}
        stepInfo={{ current: 2, total: 3, stepName: "Datos de Institución" }}
      />

      {/* Modal de Formulario de Pago - Paso 3 */}
      <PaymentFormModal
        isOpen={isPaymentModalOpen}
        onClose={closePaymentModal}
        onSubmit={handlePaymentSubmit}
        onBack={goBackToInstitutionForm}
        selectedPlan={selectedLicense}
        formData={formData}
        handleInputChange={handleInputChange}
        formatCardNumber={formatCardNumber}
        formatExpiry={formatExpiry}
        isAnnual={SHOW_TOGGLE ? isAnnual : DEFAULT_PRICING_MODE === 'annual'}
        stepInfo={{ current: 3, total: 3, stepName: "Método de Pago" }}
      />

      {/* Modal de Confirmación */}
      <ModalConfirmacion
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title="Resultado del Registro"
        message={confirmationMessage}
        isSuccess={isOperationSuccessful}
      />
    </>
  );
};

export default LicenseSection;
